





























































































import { PropType, computed, defineComponent, ref } from '@vue/composition-api';
import {
  FormCheckboxComponentConfig,
  FormSelectComponentConfig,
  FormSwitchComponentConfig,
  StepDefinition,
} from '@/interfaces/step-definition';
import { FormFieldType } from '@/consts/input-types';
import HelperTextButton from '@/components/HelperTextButton.vue';
import GcCheckbox from '@/components/primitives/GcCheckbox.vue';
import GcDatePicker from '@/components/primitives/GcDatePicker.vue';
import GcSelect from '@/components/primitives/GcSelect.vue';
import GcSlider from '@/components/primitives/GcSlider.vue';
import GcSwitch from '@/components/primitives/GcSwitch.vue';
import GcTextField from '@/components/primitives/GcTextField.vue';
import GcTextArea from '@/components/primitives/GcTextArea.vue';
import GcTimePicker from '@/components/primitives/GcTimePicker.vue';
import MultipleChoice from '@/components/MultipleChoice.vue';
import GcAlert from '@/components/primitives/GcAlert.vue';
import GcDropzone from '@/components/primitives/GcDropzone.vue';
import { getValidators } from '@/lib/forms/validators';
import { AssistantContext } from '~/steps/steps';
import HelperDialog from '@/components/HelperDialog.vue';
import {
  getComponentLabelString,
  getOptionLabelString,
} from '@/lib/forms/helper';

export default defineComponent({
  components: {
    GcAlert,
    GcCheckbox,
    GcDatePicker,
    GcDropzone,
    GcSelect,
    GcSlider,
    GcSwitch,
    GcTextArea,
    GcTextField,
    GcTimePicker,
    HelperDialog,
    HelperTextButton,
    MultipleChoice,
  },
  props: {
    assistantContext: {
      default: () => ({}),
      type: Object as PropType<AssistantContext>,
    },
    fieldName: {
      required: true,
      type: String,
    },
    formData: {
      default: () => ({}),
      required: true,
      type: Object as PropType<{ [key: string]: string }>,
    },
    hideLabel: {
      default: false,
      type: Boolean,
    },
    step: {
      default: () => ({}),
      type: Object as PropType<StepDefinition>,
    },
  },
  setup: (props, { emit }) => {
    const helpVisible = ref(false);

    const field = computed(() =>
      props.step.fields?.find((f) => f.name === props.fieldName),
    );
    const fieldLabelString = computed(
      () => `${props.step.id}.formFields.${props.fieldName}.label`,
    );

    if (!field) {
      throw Error('Could not find form field ' + props.fieldName);
    }

    const controlComponentMap: Record<number, string> = {
      [FormFieldType.Checkbox]: 'gc-checkbox',
      [FormFieldType.DatePicker]: 'gc-date-picker',
      [FormFieldType.MultipleChoice]: 'multiple-choice',
      [FormFieldType.Select]: 'gc-select',
      [FormFieldType.Slider]: 'gc-slider',
      [FormFieldType.Switch]: 'gc-switch',
      [FormFieldType.TextArea]: 'gc-text-area',
      [FormFieldType.TextField]: 'gc-text-field',
      [FormFieldType.TimePicker]: 'gc-time-picker',
      [FormFieldType.FileUpload]: 'gc-dropzone',
    };
    const controlComponent = computed(
      () => field.value && controlComponentMap[field.value.type],
    );

    const validateOnBlur = computed(
      () =>
        !!field.value &&
        [FormFieldType.TextArea, FormFieldType.TextField].includes(
          field.value.type,
        ),
    );

    // This label is used to get translated labels if needed within component like VCheckbox
    const controlComponentLabelString = computed(() =>
      field.value?.component &&
      (field.value.component as
        | FormCheckboxComponentConfig
        | FormSwitchComponentConfig).showLabel
        ? getComponentLabelString(props.step.id, props.fieldName)
        : '',
    );

    // This label is used to get translated items if needed within component like VSelect
    const controlComponentItemsStrings = computed(() =>
      field.value?.component &&
      (field.value.component as FormSelectComponentConfig).items
        ? (field.value
            .component as FormSelectComponentConfig).items.map((item) =>
            getOptionLabelString(props.step.id, props.fieldName, item),
          )
        : [],
    );

    const fieldIsVisible = (): boolean => {
      if (!field.value?.isVisible) {
        return true;
      }

      return field.value?.isVisible(props.assistantContext);
    };

    const controlValidators = computed(
      () => field.value && getValidators(field.value),
    );

    const emitNextStep = () => {
      emit('next-step');
    };

    return {
      FormFieldType,
      controlComponent,
      controlComponentItemsStrings,
      controlComponentLabelString,
      controlValidators,
      emitNextStep,
      field,
      fieldIsVisible,
      fieldLabelString,
      helpVisible,
      validateOnBlur,
    };
  },
});
